import { Box, Typography } from "@outschool/backpack";
import { SettlementError } from "@outschool/errors";
import {
  CurrencyCode,
  EnrollmentCharterParentInput,
  EnrollmentInput,
} from "@outschool/gql-backend-generated";
import {
  ActivityEnrollQueryQuery,
  AddDonationItemToOrderMutation,
  AddDonationItemToOrderMutationVariables,
  CreateEnrollmentsMutation,
  CreateEnrollmentsMutationVariables,
  CurrentUserPaymentMethodsQuery,
  FullCurrentUserFragmentFragment,
  GetLearnerItems_EnrollmentFragment,
  PricingBreakdownQueryQuery,
  SettleOrderMutation,
} from "@outschool/gql-frontend-generated";
import {
  USD_STATIC_EXCHANGE_RATE,
  useCurrencyLocalization,
  useTranslation,
} from "@outschool/localization";
import { dayjs } from "@outschool/time";
import {
  useAnalytics,
  useCheckoutStarted,
  useTrackEvent,
} from "@outschool/ui-analytics";
import { useMutation } from "@outschool/ui-apollo";
import { EsaSessionType, useSession } from "@outschool/ui-auth";
import { cleanErrorMessage } from "@outschool/ui-utils";
import _ from "lodash";
import React from "react";
import { useLocation } from "react-router";

import * as Enrollment from "../../../shared/Enrollment";
import * as EnrollmentPayment from "../../../shared/EnrollmentPayment";
import * as Section from "../../../shared/Section";
import ActionType from "../../actions/ActionType";
import { useHasEnrolled, usePaymentMethods } from "../../hooks/CurrentUser";
import { useTrackLearnerCreatedDuringEnrollment } from "../../hooks/useTrackLearnerCreatedDuringEnrollment";
import { shouldChargeInLocalCurrency } from "../../lib/Localization";
import { isOrganizationAdminWhoCanEnroll } from "../../lib/Users";
import {
  useCreditAccount,
  useEsaMembershipCreditAccount,
  useSubscriptionCreditAccount,
} from "../../queries/CreditQueries";
import { useUpdateCurrentUserMutation } from "../../queries/CurrentUser";
import { addDonationItemToOrderMutation } from "../../queries/DonationMutations";
import { createEnrollmentsMutation } from "../../queries/EnrollmentMutations";
import { useExchangeRate } from "../../queries/ExchangeRateQueries";
import {
  useAcceptPaymentWithCoupon,
  useAcceptPaymentWithPricingOffer,
  useCancelOrder,
  useSettleOrder,
} from "../../queries/PaymentMutations";
import RequestAgeRangeException from "../account/RequestAgeRangeException";
import { SavePaymentMethod } from "../account/SavePaymentMethod";
import LoadingWithMessage from "../LoadingWithMessage";
import SectionSoldOut from "../payments/SectionSoldOut";
import { StripeCheckoutForm } from "../payments/StripeCheckoutForm";
import StripeRecurringSavedPaymentForm from "../payments/StripeRecurringSavedPaymentForm";
import ActivitySummary from "./ActivitySummary";
import { shouldShowCoppa } from "./CoppaConsent";
import EnrollmentForm from "./EnrollmentForm";
import ChooseOngoingChooseStartTime from "./enrollmentForm/ChooseOngoingStartTime";
import HowDidYouHearAboutUsModal from "./HowDidYouHearAboutUsModal";
import { OrderProvider, useOrderContext } from "./OrderContext";
import SelfPacedChooseStartTime from "./SelfPacedChooseStartTime";

// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

type EnrollmentFormType = ActivityEnrollQueryQuery["enrollmentForm"];
type ActivityType = NonNullable<EnrollmentFormType>["activity"];
type SectionType = NonNullable<EnrollmentFormType>["section"];
type PendingEnrollmentsType =
  NonNullable<EnrollmentFormType>["pendingEnrollments"];
type CurrentUserType = ActivityEnrollQueryQuery["currentUser"];

// Needed because types in `useCheckoutStarted` are incompatible with GraphQL
// TODO: update `useCheckoutStarted` to accept nullable fields, or enforce non-null
type TrackedActivityType = ActivityType & {
  subject: string;
  price_cents: number;
  weekly_meetings: number;
  duration_weeks: number;
  details: {
    photo: string;
  };
};
type TrackedLeaderType = SectionType["leader"] & {
  name: string;
};

type CreatedOrSettled =
  | CreateEnrollmentsMutation["createEnrollments"]
  | SettleOrderMutation["settleOrder"];

interface ActivityEnrollWithStripeElementsPropsBase {
  activity: ActivityType;
  section: SectionType;
  enrollingParentEnrollments?: GetLearnerItems_EnrollmentFragment[];
  userTimeZone: string | null;
  payingUser: FullCurrentUserFragmentFragment;
  disabled?: boolean;
  pendingEnrollments?: PendingEnrollmentsType;
  waitlistSeatsCount?: number;
  showSummary?: boolean;
  selfPacedStartTime?: Date;
  onClose: (e?: React.SyntheticEvent, resetFields?: boolean) => void;
  onEnrollmentInProgressChange?: (value: boolean) => void;
  onEnrollmentStepChange?: (value: Step) => void;
  onPaymentComplete: ({
    enrollments,
    giftEnrollments,
    orderSlugId,
    isRecurringEnrollment,
  }: {
    enrollments: CreatedOrSettled["enrollments"];
    giftEnrollments: CreatedOrSettled["giftEnrollments"];
    orderSlugId: string;
    isRecurringEnrollment?: boolean;
  }) => void;
  onCompleteFlow: ({
    enrollments,
    giftEnrollments,
    orderSlugId,
    isRecurringEnrollment,
  }: {
    enrollments: CreatedOrSettled["enrollments"];
    giftEnrollments: CreatedOrSettled["giftEnrollments"];
    orderSlugId: string;
    isRecurringEnrollment?: boolean;
  }) => void;
  enrollingParent: CurrentUserType;
}

interface ActivityEnrollWithStripeElementsPropsNonOrganization
  extends ActivityEnrollWithStripeElementsPropsBase {
  conflictingEnrollments: GetLearnerItems_EnrollmentFragment[];
}

interface ActivityEnrollWithStripeElementsPropsOrganization
  extends ActivityEnrollWithStripeElementsPropsBase {
  charterParentInput?: EnrollmentCharterParentInput;
  organizationUid?: string;
}

export type ActivityEnrollWithStripeElementsProps =
  | ActivityEnrollWithStripeElementsPropsNonOrganization
  | ActivityEnrollWithStripeElementsPropsOrganization;

function isNonOrganizationProps(
  props: Partial<ActivityEnrollWithStripeElementsProps>,
): props is ActivityEnrollWithStripeElementsPropsNonOrganization {
  return !isOrganizationProps(props);
}

function isOrganizationProps(
  props: Partial<ActivityEnrollWithStripeElementsProps>,
): props is ActivityEnrollWithStripeElementsPropsOrganization {
  return (
    (props as ActivityEnrollWithStripeElementsPropsOrganization)
      .organizationUid !== undefined
  );
}

export enum Step {
  EnrollmentForm = "enrollmentForm",
  ShouldCreateEnrollments = "shouldCreateEnrollments",
  RequestingAgeRangeException = "requestingAgeRangeException",
  CreatingEnrollments = "creatingEnrollments",
  PayingForFreeEnrollments = "payingForFreeEnrollments",
  PaymentForm = "paymentForm",
  SavePaymentFormCoppa = "savePaymentFormCoppa",
  SavePaymentFormRecurring = "savePaymentFormRecurring",
  EnrollmentCompletePaidByUser = "enrollmentCompletePaidByUser", // paid using stripe
  EnrollmentCompletePaidByOther = "enrollmentCompletePaidByOther", // free class, purchase order, etc.
  HandlingPayment = "handlingPayment",
  HandlingFreePayment = "handlingFreePayment",
  SectionSoldOut = "seatTakenWaitlist",
  HowDidYouHearAboutUs = "howDidYouHearAboutUs",
  ChooseSelfPacedStartTime = "ChooseSelfPacedStartTime",
  FlowComplete = "flowComplete",
  PayingForEnrollment = "PayingForEnrollment",
  ChooseOngoingChooseStartTime = "ChooseOngoingChooseStartTime",
}

type Child = NonNullable<CurrentUserType>["children"][number];

interface BaseState {
  step: Step;
  error: Error | null;
  needsCoppaConfirmation: boolean;
  paymentIntent: string | null;
  orderSlugId: string | null;
  selectedLearnerForMessage: Child | null;
  postPaymentCalled: boolean;
  waitlistsEnabled?: boolean;
}

type CreatedEnrollments = {
  nonGiftEnrollments: CreateEnrollmentsMutation["createEnrollments"]["enrollments"];
  giftEnrollments: CreateEnrollmentsMutation["createEnrollments"]["giftEnrollments"];
};

interface CreatingState extends BaseState, CreatedEnrollments {
  step:
    | Step.EnrollmentForm
    | Step.ShouldCreateEnrollments
    | Step.RequestingAgeRangeException
    | Step.CreatingEnrollments
    | Step.PayingForFreeEnrollments
    | Step.PaymentForm
    | Step.SavePaymentFormCoppa
    | Step.SavePaymentFormRecurring
    | Step.HandlingPayment
    | Step.HandlingFreePayment
    | Step.SectionSoldOut
    | Step.ChooseSelfPacedStartTime
    | Step.PayingForEnrollment
    | Step.ChooseOngoingChooseStartTime;
}

type PaidEnrollments = {
  nonGiftEnrollments: CreatedOrSettled["enrollments"];
  giftEnrollments: CreatedOrSettled["giftEnrollments"];
};

interface PaidByOtherState extends BaseState, CreatedEnrollments {
  step: Step.EnrollmentCompletePaidByOther;
}

interface PaidState extends BaseState, PaidEnrollments {
  step:
    | Step.EnrollmentCompletePaidByUser
    | Step.HowDidYouHearAboutUs
    | Step.FlowComplete;
}

type State = CreatingState | PaidState | PaidByOtherState;

type Action =
  | { type: "CANCELLED" }
  | { type: "HANDLING_PAYMENT" }
  | { type: "HANDLING_FREE_PAYMENT" }
  | { type: "CREATING_ENROLLMENTS" }
  | { type: "REQUESTING_AGE_RANGE_EXCEPTION"; value: { learner: Child } }
  | { type: "ERRORED"; error: Error | null; waitlistsEnabled: boolean }
  | {
      type: "ENROLL_CLICK";
      value: {
        shouldShowCoppa: boolean;
      };
    }
  | { type: "POST_PAYMENT_CALLED" }
  | { type: "COPPA_CONFIRMED" }
  | {
      type: "ENROLLMENTS_CREATED";
      value: CreatedEnrollments & {
        orderSlugId: string;
        stripeCents: number;
        totalCents: number;
        subscriptionOutstandingBalanceCredits: number;
        pricingOffer: PricingBreakdownQueryQuery["pricingBreakdown"]["pricingOffer"];
        paymentMethods: NonNullable<
          CurrentUserPaymentMethodsQuery["currentUser"]
        >["paymentMethods"];
        isRecurringPayment: boolean;
        hasActiveSubscription: boolean;
      };
    }
  | {
      type: "PAYMENT_BY_USER_COMPLETE";
      value: PaidEnrollments;
    }
  | { type: "SECTION_SOLD_OUT" }
  | {
      type: "HOW_DID_YOU_HEAR_ABOUT_US";
      value: PaidEnrollments;
    }
  | { type: "SECTION_SOLD_OUT" }
  | { type: "SELF_PACED_START_TIME_CHOSEN" }
  | { type: "BACK_TO_SELF_PACED_CHOOSE_A_START_TIME" }
  | { type: "ONGOING_START_TIME_CHOSEN" }
  | { type: "BACK_TO_ONGOING_CHOOSE_A_START_TIME" };

function requiresSavedPaymentFormCoppa(state: State, action: Action) {
  if (action.type !== "ENROLLMENTS_CREATED") {
    throw new Error("Invalid action type: " + action.type);
  }

  const { stripeCents, pricingOffer, paymentMethods, hasActiveSubscription } =
    action.value;

  if (hasActiveSubscription) {
    return false;
  }

  if (stripeCents > 0) {
    return false;
  }

  if (pricingOffer && !pricingOffer.requirePaymentMethod) {
    return false;
  }

  return state.needsCoppaConfirmation && paymentMethods.length === 0;
}

function requiresSavedPaymentFormRecurring(action: Action) {
  if (action.type !== "ENROLLMENTS_CREATED") {
    throw new Error("Invalid action type: " + action.type);
  }

  const {
    stripeCents,
    totalCents,
    pricingOffer,
    isRecurringPayment,
    hasActiveSubscription,
  } = action.value;

  if (hasActiveSubscription) {
    return false;
  }

  if (stripeCents > 0 || totalCents === 0 || pricingOffer) {
    return false;
  }

  if (isRecurringPayment) {
    return true;
  }

  return false;
}

function shouldShowSoldOutView(action: Extract<Action, { type: "ERRORED" }>) {
  const { error } = action;
  return (
    error && cleanErrorMessage(error.message) === SettlementError.SectionSoldOut
  );
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "CANCELLED":
      return { ...initialState };
    case "ERRORED":
      const showSoldOutView = shouldShowSoldOutView(action);
      return {
        ...state,
        step: showSoldOutView ? Step.SectionSoldOut : Step.EnrollmentForm,
        error: action.error,
      };
    case "ENROLL_CLICK": {
      return {
        ...state,
        needsCoppaConfirmation: action.value.shouldShowCoppa,
        step: Step.ShouldCreateEnrollments,
      };
    }
    case "CREATING_ENROLLMENTS":
      return { ...state, step: Step.CreatingEnrollments };
    case "REQUESTING_AGE_RANGE_EXCEPTION":
      return {
        ...state,
        step: Step.RequestingAgeRangeException,
        selectedLearnerForMessage: action.value.learner,
      };
    case "ENROLLMENTS_CREATED": {
      return {
        ...state,
        ...action.value,
        step: [
          ...action.value.nonGiftEnrollments,
          ...(action.value.giftEnrollments || []),
        ].every(e => Enrollment.isValid(e)) // If the class is free (due to pricing offer or purchase order) then skip payment form
          ? Step.EnrollmentCompletePaidByOther
          : action.value.hasActiveSubscription &&
            action.value.stripeCents === 0 &&
            action.value.subscriptionOutstandingBalanceCredits === 0
          ? Step.PayingForEnrollment
          : action.value.hasActiveSubscription &&
            action.value.subscriptionOutstandingBalanceCredits > 0
          ? Step.PaymentForm
          : requiresSavedPaymentFormCoppa(state, action)
          ? Step.SavePaymentFormCoppa
          : requiresSavedPaymentFormRecurring(action)
          ? Step.SavePaymentFormRecurring
          : action.value.stripeCents === 0
          ? Step.PayingForFreeEnrollments
          : Step.PaymentForm,
      };
    }
    case "HANDLING_PAYMENT":
      return { ...state, step: Step.HandlingPayment };
    case "HANDLING_FREE_PAYMENT":
      return { ...state, step: Step.HandlingFreePayment };
    case "PAYMENT_BY_USER_COMPLETE": {
      return {
        ...state,
        ...action.value,
        step: Step.EnrollmentCompletePaidByUser,
      };
    }
    case "POST_PAYMENT_CALLED":
      return {
        ...state,
        postPaymentCalled: true,
      };
    case "SECTION_SOLD_OUT":
      return {
        ...state,
        step: Step.SectionSoldOut,
      };
    case "HOW_DID_YOU_HEAR_ABOUT_US":
      return {
        ...state,
        ...action.value,
        step: Step.HowDidYouHearAboutUs,
      };
    case "SELF_PACED_START_TIME_CHOSEN":
      return {
        ...state,
        step: Step.EnrollmentForm,
      };
    case "BACK_TO_SELF_PACED_CHOOSE_A_START_TIME":
      return {
        ...state,
        step: Step.ChooseSelfPacedStartTime,
      };
    case "ONGOING_START_TIME_CHOSEN":
      return {
        ...state,
        step: Step.EnrollmentForm,
      };
    case "BACK_TO_ONGOING_CHOOSE_A_START_TIME":
      return {
        ...state,
        step: Step.ChooseOngoingChooseStartTime,
      };
    default:
      return state;
  }
}

const initialState = {
  error: null,
  step: Step.EnrollmentForm,
  orderSlugId: null,
  paymentIntent: null,
  nonGiftEnrollments: [],
  giftEnrollments: [],
  selectedLearnerForMessage: null,
  postPaymentCalled: false,
  needsCoppaConfirmation: false,
};

function ActivityEnrollWithStripeElements({
  activity,
  section,
  enrollingParentEnrollments,
  waitlistSeatsCount,
  payingUser,
  disabled,
  showSummary = true,
  onPaymentComplete = ({}) => {},
  onCompleteFlow = () => {},
  onClose,
  onEnrollmentInProgressChange = () => {},
  onEnrollmentStepChange = () => {},
  enrollingParent,
  ...otherProps
}: ActivityEnrollWithStripeElementsProps) {
  const { currentUser, esaSessionType } = useSession();
  const isClassWalletSession = esaSessionType === EsaSessionType.CW;
  const analytics = useAnalytics();
  const { t } = useTranslation(
    "client\\components\\enrolling\\ActivityEnrollWithStripeElements",
  );
  // props specific to charter flow
  const { charterParentInput = undefined, organizationUid = undefined } =
    isOrganizationProps(otherProps) ? otherProps : {};

  // props specific to non-charter flow
  const conflictingEnrollments = isNonOrganizationProps(otherProps)
    ? otherProps?.conflictingEnrollments
    : undefined;

  const enrollingOrgAdminUser = isOrganizationAdminWhoCanEnroll(payingUser)
    ? payingUser
    : null;

  // Note: charter purchases would also fall under this, but we don't send them through this component
  const isPrepayUser = !!enrollingOrgAdminUser || isClassWalletSession;
  const isOrgAdminEnrollment = enrollingOrgAdminUser && !isPrepayUser;
  const allowsFutureOngoingEnrollment =
    !!activity.allowsFutureOngoingEnrollments &&
    !isOrgAdminEnrollment &&
    !isPrepayUser;

  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    step: activity.isSelfPaced
      ? Step.ChooseSelfPacedStartTime
      : allowsFutureOngoingEnrollment
      ? Step.ChooseOngoingChooseStartTime
      : Step.EnrollmentForm,
  });

  const location = useLocation();
  const [createEnrollments, { loading: enrollmentInProgress }] = useMutation<
    CreateEnrollmentsMutation,
    CreateEnrollmentsMutationVariables
  >(createEnrollmentsMutation);
  const [addDonationItemToOrder] = useMutation<
    AddDonationItemToOrderMutation,
    AddDonationItemToOrderMutationVariables
  >(addDonationItemToOrderMutation);
  const { paymentMethods, loading: arePaymentMethodsLoading } =
    usePaymentMethods();
  const { loading: isCreditAccountLoading, acceptPaymentWithStoredCredit } =
    useCreditAccount();
  const {
    loading: isSubscriptionAccountLoading,
    acceptPaymentWithSubscriptionCredit,
  } = useSubscriptionCreditAccount();
  const {
    loading: isEsaMembershipAccountLoading,
    acceptPaymentWithEsaMembershipCredit,
  } = useEsaMembershipCreditAccount();
  const trackEvent = useTrackEvent();
  const trackCheckoutStarted = useCheckoutStarted();
  const trackLearnerCreated = useTrackLearnerCreatedDuringEnrollment();

  const hasActiveSubscription = !!currentUser?.canPayWithSubscriptionCredits;
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const settleOrder = useSettleOrder();
  const cancelOrder = useCancelOrder();
  const [selfPacedStartTime, setSelfPacedStartTime] = React.useState(
    otherProps.selfPacedStartTime || new Date(),
  );
  React.useEffect(() => {
    onEnrollmentStepChange(state.step);
  }, [onEnrollmentStepChange, state.step]);

  const {
    pricingBreakdown,
    isPricingBreakdownLoading,
    refetchPricingBreakdown,
    enrollmentInputs,
    setEnrollmentInputs,
    setDonationAmountCents,
    donationAmountCents,
    promoCode,
    currencyCode,
    exchangeRate,
    ongoingStartTime,
    ongoingEndTime,
    isWeeklyPayment,
    hasEsaMembership,
    isPrepaidOngoingEnrollment,
    setIsPrepaidOngoingEnrollment,
    setOngoingStartTime,
  } = useOrderContext();

  React.useEffect(() => {
    setIsPrepaidOngoingEnrollment?.(isPrepayUser || !!isOrgAdminEnrollment);
  }, [isPrepayUser, isOrgAdminEnrollment, setIsPrepaidOngoingEnrollment]);

  const pricingOffer = pricingBreakdown?.pricingOffer;

  const acceptPaymentWithPricingOffer = useAcceptPaymentWithPricingOffer({
    // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
    orderSlugId: state.orderSlugId,
  });

  const isChargingInLocalCurrency = shouldChargeInLocalCurrency(currencyCode);
  const currencyCodeToWrite = isChargingInLocalCurrency
    ? currencyCode
    : CurrencyCode.Usd;
  const exchangeRateToWrite = isChargingInLocalCurrency
    ? exchangeRate
    : USD_STATIC_EXCHANGE_RATE;

  const acceptPaymentWithCoupon = useAcceptPaymentWithCoupon({
    // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
    orderSlugId: state.orderSlugId,
    activityUid: activity.uid,
    amountCents: pricingBreakdown?.couponCents ?? 0,
    // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    code: promoCode,
    currencyCode: currencyCodeToWrite,
    exchangeRate: exchangeRateToWrite,
  });

  const { hasEnrolled } = useHasEnrolled();

  // EVENT HANDLERS
  const handleEnrollClick = React.useCallback(
    (newEnrollmentInputs: EnrollmentInput[], donationAmountCents: number) => {
      setEnrollmentInputs(newEnrollmentInputs);
      setDonationAmountCents(donationAmountCents);
      dispatch({
        type: "ENROLL_CLICK",
        value: {
          shouldShowCoppa: shouldShowCoppa({
            // @ts-ignore TS(2322): Type '(InputMaybe<EnrollmentLearnerInput> | undefi... Remove this comment to see the full error message
            learners: _.map(newEnrollmentInputs, "learner"),
            // @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; consented... Remove this comment to see the full error message
            parent: enrollingParent,
            activity,
          }),
        },
      });
    },
    [
      dispatch,
      enrollingParent,
      setDonationAmountCents,
      setEnrollmentInputs,
      activity,
    ],
  );

  const handleCreateEnrollments = React.useCallback(async () => {
    if (isPricingBreakdownLoading) {
      return;
    }
    dispatch({ type: "CREATING_ENROLLMENTS" });
    try {
      const attribution = await Promise.race([
        analytics.attribution(),
        new Promise(resolve => {
          setTimeout(() => resolve(undefined), 1000);
        }),
      ]);
      if (!attribution) {
        OsPlatform.captureMessage(
          "Analytics attribution call timed out during enrollment flow",
        );
      }

      const currentUser = structuredClone(enrollingParent);

      const {
        data: {
          // @ts-ignore TS(2339): Property 'createEnrollments' does not exist on typ... Remove this comment to see the full error message
          createEnrollments: {
            enrollments: nonGiftEnrollments,
            giftEnrollments = [],
            orderSlugId,
          },
        },
      } = await createEnrollments({
        variables: {
          orderInput: {
            activityUid: activity.uid,
            sectionUid: section?.uid,
            enrollmentInputs,
            // @ts-ignore TS(2322): Type 'unknown' is not assignable to type 'InputMay... Remove this comment to see the full error message
            attribution,
            impressionId: (
              location?.state as undefined | { impressionId: string }
            )?.impressionId,
            organizationUid,
            charterParentInput,
            currency_code: currencyCodeToWrite,
            exchange_rate: exchangeRateToWrite,
            ongoingStartTime,
            ongoingEndTime,
            isWeeklyPayment,
            selfPacedStartTime,
          },
          isPrepay: !!isPrepaidOngoingEnrollment,
        },
      });

      if (currentUser) {
        trackLearnerCreated(enrollmentInputs, currentUser);
      }

      if (donationAmountCents) {
        // We should add donation amount cents to order created for enrollment.
        await addDonationItemToOrder({
          variables: {
            donationInput: {
              orderSlugId,
              amountCents: donationAmountCents,
            },
          },
        });
      }

      dispatch({
        type: "ENROLLMENTS_CREATED",
        value: {
          nonGiftEnrollments,
          giftEnrollments,
          orderSlugId,
          // @ts-ignore TS(2322): Type 'number | undefined'
          subscriptionOutstandingBalanceCredits:
            pricingBreakdown?.subscriptionOutstandingBalanceCredits,
          hasActiveSubscription: hasActiveSubscription,
          // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to t... Remove this comment to see the full error message
          stripeCents: pricingBreakdown.stripeCents,
          // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to t... Remove this comment to see the full error message
          totalCents: pricingBreakdown.totalCents,
          // @ts-ignore TS(2322): Type '{ __typename?: "PricingOffer" | undefined; u... Remove this comment to see the full error message
          pricingOffer,
          paymentMethods,
          isRecurringPayment:
            (!!activity.is_ongoing_weekly && !organizationUid) ||
            activity.isClub ||
            isWeeklyPayment,
        },
      });
    } catch (error) {
      dispatch({
        type: "ERRORED",
        error,
        waitlistsEnabled: !!activity.allowWaitlist,
      });
    }
  }, [
    activity.uid,
    addDonationItemToOrder,
    analytics,
    charterParentInput,
    createEnrollments,
    currencyCodeToWrite,
    dispatch,
    donationAmountCents,
    enrollmentInputs,
    exchangeRateToWrite,
    isPricingBreakdownLoading,
    location,
    ongoingStartTime,
    ongoingEndTime,
    organizationUid,
    paymentMethods,
    pricingOffer,
    section?.uid,
    isWeeklyPayment,
    activity.is_ongoing_weekly,
    activity.isClub,
    activity.allowWaitlist,
    selfPacedStartTime,
    hasActiveSubscription,
    pricingBreakdown?.stripeCents,
    pricingBreakdown?.totalCents,
    pricingBreakdown?.subscriptionOutstandingBalanceCredits,
    isPrepaidOngoingEnrollment,
    trackLearnerCreated,
    enrollingParent,
  ]);

  const acceptPaymentsWithTenderTypes = React.useCallback(async () => {
    if (isPricingBreakdownLoading) {
      return;
    }
    if (
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      pricingBreakdown.stripeCents === 0 ||
      (hasActiveSubscription && pricingBreakdown?.subscriptionCredits === 0)
    ) {
      dispatch({ type: "HANDLING_FREE_PAYMENT" });
    } else {
      dispatch({ type: "HANDLING_PAYMENT" });
    }
    try {
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      if (!!pricingBreakdown.couponCents) {
        await acceptPaymentWithCoupon();
      }
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      if (!!pricingBreakdown.pricingOfferCents) {
        await acceptPaymentWithPricingOffer({
          // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
          pricingOfferUid: pricingOffer?.uid,
          // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
          amountCents: pricingBreakdown?.pricingOfferCents,
        });
      }

      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      if (!!pricingBreakdown.storedCreditCents) {
        await acceptPaymentWithStoredCredit(
          // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
          state.orderSlugId,
          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          pricingBreakdown.storedCreditCents,
          currencyCodeToWrite,
          exchangeRateToWrite,
        );
      }

      if (!!pricingBreakdown?.subscriptionCredits) {
        await acceptPaymentWithSubscriptionCredit(
          // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
          state.orderSlugId,
          // @ts-ignore TS(2532): Object is possibly 'undefined'.
          pricingBreakdown.subscriptionCredits,
          currencyCodeToWrite,
          exchangeRateToWrite,
        );
      }

      const learners = _.map(enrollmentInputs, "learner");
      if (!!pricingBreakdown?.esaMembershipCredits) {
        if (learners.length > 1) {
          throw new Error(
            "ESA Membership credits only apply to single learner orders",
          );
        }

        if (learners.length === 1 && !!learners[0]?.uid) {
          await acceptPaymentWithEsaMembershipCredit(
            // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
            state.orderSlugId,
            // @ts-ignore TS(2532): Object is possibly 'undefined'.
            pricingBreakdown.esaMembershipCredits,
            currencyCodeToWrite,
            exchangeRateToWrite,
            learners[0]?.uid,
          );
        }
      }

      const { enrollments, giftEnrollments } = await settleOrder(
        // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        state.orderSlugId,
        undefined,
        isPrepaidOngoingEnrollment ? ongoingStartTime : undefined,
      );

      const enrollmentPayments = _.flatten(
        enrollments.map((enrollment: $TSFixMe) => enrollment.payments),
      );
      trackEvent(ActionType.Payment.CREATE + "_COMPLETED", {
        activity_uid: activity.uid,
        user_uid: payingUser.uid,
        // @ts-ignore TS(2345): Argument of type 'unknown[]' is not assignable to ... Remove this comment to see the full error message
        amount: EnrollmentPayment.totalPaidCents(enrollmentPayments),
      });

      dispatch({
        type: "PAYMENT_BY_USER_COMPLETE",
        value: {
          nonGiftEnrollments: enrollments,
          giftEnrollments,
        },
      });
    } catch (error) {
      trackEvent(ActionType.Payment.CREATE + "_FAILED", { error });
      try {
        // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        await cancelOrder(state.orderSlugId);
        await refetchPricingBreakdown();
      } catch (cancelError) {
        OsPlatform.captureError(cancelError, {
          extra: {
            originalError: error,
            orderSlugId: state.orderSlugId,
          },
        });
      } finally {
        // Ensure we always return to create enrollment state even if cancelOrder is broken
        dispatch({
          type: "ERRORED",
          error,
          waitlistsEnabled: !!activity.allowWaitlist,
        });
      }
    }
  }, [
    trackEvent,
    dispatch,
    settleOrder,
    activity,
    payingUser,
    ongoingStartTime,
    state.orderSlugId,
    acceptPaymentWithPricingOffer,
    acceptPaymentWithStoredCredit,
    acceptPaymentWithSubscriptionCredit,
    acceptPaymentWithEsaMembershipCredit,
    acceptPaymentWithCoupon,
    refetchPricingBreakdown,
    pricingOffer?.uid,
    isPricingBreakdownLoading,
    cancelOrder,
    currencyCodeToWrite,
    exchangeRateToWrite,
    hasActiveSubscription,
    enrollmentInputs,
    pricingBreakdown?.esaMembershipCredits,
    pricingBreakdown?.pricingOfferCents,
    pricingBreakdown?.storedCreditCents,
    pricingBreakdown?.stripeCents,
    pricingBreakdown?.couponCents,
    pricingBreakdown?.subscriptionCredits,
    isPrepaidOngoingEnrollment,
  ]);

  const handlePaymentComplete = React.useCallback(async () => {
    if (state.postPaymentCalled) {
      return;
    }

    dispatch({ type: "POST_PAYMENT_CALLED" });

    try {
      if (state.needsCoppaConfirmation) {
        await updateCurrentUser({
          variables: { consented_to_coppa_notice_at: dayjs().utc().toDate() },
        });
      }

      const shouldCheckForSavedPayment =
        (!pricingBreakdown?.subscriptionOutstandingBalanceCredits ||
          pricingBreakdown.subscriptionOutstandingBalanceCredits <= 0) &&
        ((!!activity.is_ongoing_weekly && !organizationUid) ||
          activity.isClub ||
          isWeeklyPayment);

      onPaymentComplete({
        enrollments: state.nonGiftEnrollments,
        giftEnrollments: state.giftEnrollments,
        // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
        orderSlugId: state.orderSlugId,
        isRecurringEnrollment: shouldCheckForSavedPayment,
      });
    } catch (error) {
      OsPlatform.captureError(error);
    }
  }, [
    updateCurrentUser,
    pricingBreakdown,
    activity,
    isWeeklyPayment,
    organizationUid,
    state,
    onPaymentComplete,
  ]);

  const handleCheckoutFormCancel = React.useCallback(() => {
    dispatch({ type: "CANCELLED" });
  }, [dispatch]);

  const handleRequestOutOfRangeEnrollment = React.useCallback(
    (learner: Child) => {
      dispatch({
        type: "REQUESTING_AGE_RANGE_EXCEPTION",
        value: { learner },
      });
    },
    [dispatch],
  );

  // STATE TRANSITION EFFECTS
  // create enrollments
  React.useEffect(() => {
    if (
      state.step === Step.ShouldCreateEnrollments &&
      pricingBreakdown &&
      !isPricingBreakdownLoading
    ) {
      handleCreateEnrollments();
    }
  }, [
    pricingBreakdown,
    state.step,
    handleCreateEnrollments,
    isPricingBreakdownLoading,
  ]);

  // show payment form
  React.useEffect(() => {
    if (state.step === Step.PaymentForm && !isPricingBreakdownLoading) {
      trackCheckoutStarted({
        activityListing: {
          activity: activity as TrackedActivityType,
          leader: (section?.leader || activity.leader) as TrackedLeaderType,
        },
        // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
        orderSlugId: state.orderSlugId,
        currencyCode,
        // @ts-ignore TS(2532): Object is possibly 'undefined'.
        totalCents: pricingBreakdown.totalCents,
      });
    }
  }, [
    activity,
    currencyCode,
    isPricingBreakdownLoading,
    state.orderSlugId,
    pricingBreakdown,
    section?.leader,
    state.step,
    trackCheckoutStarted,
  ]);

  React.useEffect(() => {
    if (
      (state.step === Step.PayingForFreeEnrollments ||
        state.step == Step.PayingForEnrollment) &&
      pricingBreakdown &&
      !isPricingBreakdownLoading
    ) {
      acceptPaymentsWithTenderTypes();
    }
  }, [
    pricingBreakdown,
    state.step,
    acceptPaymentsWithTenderTypes,
    isPricingBreakdownLoading,
  ]);

  // handle payment complete
  React.useEffect(() => {
    const shouldCheckForSavedPayment =
      (!pricingBreakdown?.subscriptionOutstandingBalanceCredits ||
        pricingBreakdown.subscriptionOutstandingBalanceCredits <= 0) &&
      ((!!activity.is_ongoing_weekly && !organizationUid) ||
        activity.isClub ||
        isWeeklyPayment);

    if (
      state.step === Step.EnrollmentCompletePaidByOther ||
      state.step === Step.EnrollmentCompletePaidByUser
    ) {
      handlePaymentComplete().then(() => {
        if (!hasEnrolled && !charterParentInput && !hasEsaMembership) {
          /**
           * If the user has not enrolled in any classes and they are not from charter school,
           * show the "How did you hear about us?" modal
           */
          dispatch({
            type: "HOW_DID_YOU_HEAR_ABOUT_US",
            value: {
              ...state,
              nonGiftEnrollments: state.nonGiftEnrollments,
              giftEnrollments: state.giftEnrollments,
            },
          });
        } else {
          onCompleteFlow({
            orderSlugId: state.orderSlugId || "",
            enrollments: state.nonGiftEnrollments,
            giftEnrollments: state.giftEnrollments,
            isRecurringEnrollment: shouldCheckForSavedPayment,
          });
        }
      });
    } else if (state.step === Step.FlowComplete) {
      onCompleteFlow({
        orderSlugId: state.orderSlugId || "",
        enrollments: state.nonGiftEnrollments,
        giftEnrollments: state.giftEnrollments,
        isRecurringEnrollment: shouldCheckForSavedPayment,
      });
    }
  }, [
    activity,
    pricingBreakdown,
    organizationUid,
    isWeeklyPayment,
    state,
    state.step,
    handlePaymentComplete,
    hasEnrolled,
    onCompleteFlow,
    charterParentInput,
    hasActiveSubscription,
    hasEsaMembership,
  ]);

  // enrollment in progress change
  React.useEffect(() => {
    if (
      [
        Step.CreatingEnrollments,
        Step.PaymentForm,
        Step.SavePaymentFormCoppa,
      ].includes(state.step)
    ) {
      onEnrollmentInProgressChange(true);
    } else if (
      [
        Step.EnrollmentCompletePaidByOther,
        Step.EnrollmentCompletePaidByUser,
        Step.EnrollmentForm,
      ].includes(state.step)
    ) {
      onEnrollmentInProgressChange(false);
    }
  }, [state.step, onEnrollmentInProgressChange]);

  // Redirect to "sold out" view if form is loaded when section is already full
  React.useEffect(() => {
    const isFull =
      !activity.isSelfPaced &&
      Section.isFull(section, section?.filledSpaceCount);

    if (isFull && state.step !== Step.HowDidYouHearAboutUs) {
      dispatch({ type: "SECTION_SOLD_OUT" });
    }
  }, [activity, section, state.step]);

  return (
    <React.Fragment>
      {state.step === Step.HowDidYouHearAboutUs && (
        <Box sx={{ justifyContent: "left" }}>
          <Typography
            variant="h5"
            sx={{ margin: "10px auto", fontWeight: "bold" }}
          >
            {t("Thanks for your enrollment!")}
          </Typography>
        </Box>
      )}
      {showSummary &&
        ![
          Step.RequestingAgeRangeException,
          Step.ChooseSelfPacedStartTime,
          Step.ChooseOngoingChooseStartTime,
        ].includes(state.step) && (
          <ActivitySummary
            activity={activity}
            section={section}
            startTime={section?.start_time}
            endTime={section?.end_time}
            nextOngoingMeeting={section?.nextOngoingMeeting}
            meetings={section?.meetings}
          />
        )}
      {state.step === Step.ChooseSelfPacedStartTime && (
        <Box>
          <SelfPacedChooseStartTime
            activity={activity}
            selectedStartTime={selfPacedStartTime}
            onSelectStartTime={(startTime: Date) => {
              setSelfPacedStartTime(startTime);
              dispatch({ type: "SELF_PACED_START_TIME_CHOSEN" });
            }}
          />
        </Box>
      )}
      {state.step === Step.ChooseOngoingChooseStartTime && (
        <ChooseOngoingChooseStartTime
          section={section}
          onStartTimeSelected={(startTime: Date | null) => {
            setOngoingStartTime(startTime);
            dispatch({ type: "ONGOING_START_TIME_CHOSEN" });
          }}
        />
      )}
      {state.step === Step.EnrollmentForm && (
        <Box data-test-id="activity-enrollment-form">
          <EnrollmentForm
            // @ts-ignore TS(2322): Type 'FullCurrentUserFragmentFragment | null' is n... Remove this comment to see the full error message
            enrollingOrgAdminUser={enrollingOrgAdminUser}
            disabled={
              disabled ||
              arePaymentMethodsLoading ||
              isCreditAccountLoading ||
              isSubscriptionAccountLoading ||
              isEsaMembershipAccountLoading ||
              isPricingBreakdownLoading
            }
            enrollmentInProgress={enrollmentInProgress}
            disableOnSubmit={false}
            activity={activity}
            section={section}
            leader={section?.leader || activity.leader}
            enrollingParentEnrollments={enrollingParentEnrollments || []}
            lastSubmittedEnrollments={
              (enrollingParent && enrollingParent.lastSubmittedEnrollments) ||
              []
            }
            allowsFutureOngoingEnrollment={allowsFutureOngoingEnrollment}
            requestAgeRangeException={handleRequestOutOfRangeEnrollment}
            filledSpaceCount={section?.filledSpaceCount}
            onClose={
              activity.isSelfPaced
                ? () =>
                    dispatch({ type: "BACK_TO_SELF_PACED_CHOOSE_A_START_TIME" })
                : allowsFutureOngoingEnrollment
                ? () =>
                    dispatch({
                      type: "BACK_TO_ONGOING_CHOOSE_A_START_TIME",
                    })
                : onClose
            }
            error={state.error}
            onEnrollClick={handleEnrollClick}
            enrollingParent={enrollingParent}
            conflictingEnrollments={conflictingEnrollments}
          />
        </Box>
      )}
      {[
        Step.CreatingEnrollments,
        Step.ShouldCreateEnrollments,
        Step.HandlingFreePayment,
        Step.EnrollmentCompletePaidByUser,
        Step.EnrollmentCompletePaidByOther,
      ].includes(state.step) && (
        <LoadingWithMessage>
          {t`Please wait while we process your enrollment. Please DO NOT close this \
screen or hit the back button.`}
        </LoadingWithMessage>
      )}
      {state.step === Step.RequestingAgeRangeException && (
        <RequestAgeRangeException
          activity={activity}
          section={section}
          learner={state.selectedLearnerForMessage}
          onCancel={handleCheckoutFormCancel}
          onSend={onClose}
        />
      )}
      {[Step.PaymentForm, Step.HandlingPayment].includes(state.step) && (
        <StripeCheckoutForm
          isPayingOutstandingCreditsBalance={
            hasActiveSubscription &&
            !!pricingBreakdown?.subscriptionOutstandingBalanceCredits &&
            pricingBreakdown.subscriptionOutstandingBalanceCredits > 0
          }
          onCancel={handleCheckoutFormCancel}
          onStripePaymentAccepted={acceptPaymentsWithTenderTypes}
          // @ts-ignore TS(2322): Type 'FullCurrentUserFragmentFragment' is not assi... Remove this comment to see the full error message
          payingUser={payingUser}
          chargeInLocalCurrency
          // @ts-ignore TS(2322): Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
          amountCents={pricingBreakdown?.stripeCents}
          // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
          orderSlugId={state.orderSlugId}
          isSavedPaymentMethodRequired={
            !organizationUid &&
            (!!activity.is_ongoing_weekly || activity.isClub || isWeeklyPayment)
          }
          charterSchoolUid={organizationUid}
          activity={activity}
          isWeeklyPayment={isWeeklyPayment && !organizationUid}
          enrollingOrgAdminUser={enrollingOrgAdminUser}
        />
      )}
      {state.step === Step.SavePaymentFormCoppa && (
        <SavePaymentMethod
          isCoppaConsentCheck={true}
          formOptions={{
            title: t`Credit Card Verification`,
            description: t`You won’t be charged, but you may see a temporary $0 or $1 entry on your statement that will be removed within 3 days.`,
            buttonLabel: t`Verify Card`,
          }}
          onPaymentSaved={acceptPaymentsWithTenderTypes}
          onGoBack={handleCheckoutFormCancel}
        />
      )}
      {state.step === Step.SavePaymentFormRecurring && (
        <StripeRecurringSavedPaymentForm
          activity={activity}
          isWeeklyPayment={isWeeklyPayment}
          handleStripePaymentAccepted={acceptPaymentsWithTenderTypes}
          handleCheckoutFormCancel={handleCheckoutFormCancel}
        />
      )}
      {state.step === Step.HowDidYouHearAboutUs && (
        <HowDidYouHearAboutUsModal
          handleFlowComplete={() =>
            onCompleteFlow({
              enrollments: state.nonGiftEnrollments,
              giftEnrollments: state.giftEnrollments,
              orderSlugId: state.orderSlugId || "",
            })
          }
        />
      )}
      {state.step === Step.SectionSoldOut && (
        <SectionSoldOut
          activity={activity}
          section={section}
          waitlistSeatsCount={waitlistSeatsCount}
          parentEnrollments={enrollingParentEnrollments}
          parentLearners={enrollingParent?.children}
          onClose={onClose}
        />
      )}
    </React.Fragment>
  );
}

export default function ActivityEnrollWithStripeElementsWrapper(
  props: ActivityEnrollWithStripeElementsProps,
) {
  const { currencyCode } = useCurrencyLocalization();
  const { exchangeRate } = useExchangeRate(currencyCode);
  // props specific to non-charter flow
  const conflictingEnrollments = isNonOrganizationProps(props)
    ? props.conflictingEnrollments
    : null;
  const orgAdminEnrollingUser = isOrganizationAdminWhoCanEnroll(
    props.payingUser,
  )
    ? props.payingUser
    : null;
  const userHasEsaMembership = !orgAdminEnrollingUser
    ? !!props.enrollingParent?.hasEsaMembership
    : false;
  return (
    <OrderProvider
      // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      buyingUserUid={orgAdminEnrollingUser?.uid || props.enrollingParent?.uid}
      activity={props.activity}
      section={props.section}
      enrollingParent={props.enrollingParent}
      lastSubmittedEnrollments={props.enrollingParent?.lastSubmittedEnrollments}
      enrollmentsInOtherSections={[]}
      conflictingEnrollments={conflictingEnrollments ?? undefined}
      enrollingParentEnrollments={props.enrollingParentEnrollments}
      pendingEnrollments={props.pendingEnrollments}
      currencyCode={currencyCode}
      exchangeRate={exchangeRate}
      ignoreStrictAgeRange={Section.ignoresStrictAgeRange(props.section)}
      sectionPrepaidOngoingPeriodOptions={
        props.section?.prepaidOngoingPeriodOptions
      }
      hasEsaMembership={userHasEsaMembership}
    >
      <ActivityEnrollWithStripeElements {...props} />
    </OrderProvider>
  );
}
